import { useState } from "react";
import { useSelector } from "react-redux";

import { Menu } from "@mantine/core";

import DisabledReason from "@/components/DisabledReason";
import {
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES,
  QuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import { EncounterLaborQuickButtonsProps } from "@/encounters/components/EncounterLabor/EncounterLabor/LaborMenu/EncounterLaborQuickButtons";
import { EncounterLaborQuickButtonDrawer } from "@/encounters/components/EncounterLabor/EncounterLaborDrawer/EncounterLaborQuickButtonDrawer";
import { useBirthObservations } from "@/encounters/components/EncounterLabor/hooks/useBirthObservations";
import { selectBirthDateTime } from "@/store/slices/birth";

const FLOW_SHEET_DROPDOWN_ITEMS = [
  {
    questionnaireName: QuestionnaireType.Resuscitation,
    displayTitle: LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.Resuscitation],
  },
  {
    questionnaireName: QuestionnaireType.ShoulderDystocia,
    displayTitle: LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.ShoulderDystocia],
  },
  {
    questionnaireName: QuestionnaireType.PostpartumHemorrhage,
    displayTitle: LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.PostpartumHemorrhage],
  },
] as const;

type FlowSheet = (typeof FLOW_SHEET_DROPDOWN_ITEMS)[number];

interface SOSMenuItemsProps extends EncounterLaborQuickButtonsProps {
  onRefresh?: () => Promise<void>;
}

export default function SOSMenuItems({ encounterId, clientId, onRefresh }: SOSMenuItemsProps) {
  const [opened, setOpened] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState<FlowSheet | null>(null);
  const birthDateTime = useSelector(selectBirthDateTime);
  const { setOptimisticBirth, clearOptimisticBirth } = useBirthObservations(encounterId, clientId);

  const handleClose = () => {
    setOpened(false);
    setSelectedFlow(null);
  };

  return (
    <>
      <Menu.Dropdown data-testid="sos-menu-dropdown">
        {FLOW_SHEET_DROPDOWN_ITEMS.map((item) => {
          const isWaitingForBirth =
            !birthDateTime && item.questionnaireName === QuestionnaireType.PostpartumHemorrhage;
          return (
            <DisabledReason
              isBlock
              key={item.questionnaireName}
              reason={isWaitingForBirth ? "Enabled After Birth" : undefined}
            >
              <Menu.Item
                disabled={isWaitingForBirth}
                data-testid={`labor-menu-item-${item.questionnaireName}`}
                onClick={() => {
                  setSelectedFlow(item);
                  setOpened(true);
                }}
              >
                {item.displayTitle}
              </Menu.Item>
            </DisabledReason>
          );
        })}
      </Menu.Dropdown>

      {selectedFlow && (
        <EncounterLaborQuickButtonDrawer
          opened={opened}
          clientId={clientId}
          onClose={handleClose}
          onRefresh={onRefresh}
          encounterId={encounterId}
          onBirthCreate={setOptimisticBirth}
          questionnaireTitle={selectedFlow.displayTitle}
          questionnaireName={selectedFlow.questionnaireName}
          onClearOptimisticBirthDateTime={clearOptimisticBirth}
        />
      )}
    </>
  );
}
